import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Radar from './components/Radar';
import { createRoom, joinRoom, getRoomStatus, uploadFile, getStreamUrl } from './api';

// Simple helper to get/create a deviceId
const getDeviceId = () => {
    let id = localStorage.getItem('streamdrop_device_id');
    if (!id) {
        id = Math.random().toString(36).substring(2, 11);
        localStorage.setItem('streamdrop_device_id', id);
    }
    return id;
};

const getDeviceName = () => {
    if (window.innerWidth < 768) return "Mobile Device";
    return "PC / Laptop";
};

function App() {
    const [view, setView] = useState('home'); // home, sender, receiver
    const [pin, setPin] = useState(null);
    const [status, setStatus] = useState('Waiting...');
    const [peers, setPeers] = useState({}); // { id: { name, last_seen, role } }
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFile, setUploadingFile] = useState(null);
    const [pinInput, setPinInput] = useState('');

    const pollTimer = useRef(null);
    const deviceId = useRef(getDeviceId()).current;

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const f = urlParams.get('f');
        if (f) {
            setView('receiver');
            setFiles([{ file_id: f, filename: 'Shared File', size: 0, mime_type: '' }]);
            setStatus('Shared File Ready');
        }
    }, []);

    const startPolling = (currentPin) => {
        if (pollTimer.current) clearInterval(pollTimer.current);
        
        const poll = async () => {
            // Stop polling if the tab is hidden to save network/server calls
            if (document.hidden) return;
            
            try {
                const res = await getRoomStatus(currentPin, deviceId);
                const data = res.data;
                setPeers(data.peers || {});
                setFiles(data.files || []);
                if (data.state === 'connected' || Object.values(data.peers || {}).length > 0) {
                    setStatus('Connected ✓');
                }
            } catch (err) {
                console.error("Polling error:", err);
            }
        };

        // Poll immediately once, then set interval
        poll();
        pollTimer.current = setInterval(poll, 4000); 
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollTimer.current) clearInterval(pollTimer.current);
        };
    }, []);

    const handleStartSend = async () => {
        try {
            const res = await createRoom();
            const newPin = res.data.pin;
            setPin(newPin);
            setView('sender');
            
            // For the sender, also join the room so they appear on the radar
            await joinRoom(newPin, deviceId, getDeviceName(), "sender");
            
            startPolling(newPin);
        } catch (err) {
            alert("Failed to create room");
        }
    };

    const handleStartReceive = () => {
        setView('receiver');
    };

    const handleJoin = async () => {
        if (pinInput.length !== 6) return;
        try {
            await joinRoom(pinInput, deviceId, getDeviceName(), "receiver");
            setPin(pinInput);
            setStatus('Connecting...');
            startPolling(pinInput);
        } catch (err) {
            alert("Invalid PIN or room expired");
        }
    };

    const onFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingFile(file.name);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('room_pin', pin);

        try {
            await uploadFile(formData, (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            });
            setUploadingFile(null);
            setUploadProgress(0);
        } catch (err) {
            alert("Upload failed");
            setUploadingFile(null);
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const dc = 1;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dc)) + ' ' + sizes[i];
    };

    const getDownloadUrl = (file_id) => `${getStreamUrl(file_id)}?dl=1`;
    const getViewUrl = (file_id) => `${getStreamUrl(file_id)}?dl=0`;

    const renderHome = () => (
        <div className="home-content fade-up">
            <div className="home-eyebrow">No Login · No Cloud · Just Share</div>
            <h1 className="home-title">Drop files.<br /><span className="highlight">Instantly.</span></h1>
            <p className="home-sub">Transfer files directly between your devices — fast, private, and effortless.</p>
            <div className="role-cards">
                <div className="role-card send" onClick={handleStartSend}>
                    <div className="role-icon">📤</div>
                    <h3>Share Files</h3>
                    <p>From your PC. A 6-digit PIN appears — share it with receivers.</p>
                    <div className="role-arrow">→</div>
                </div>
                <div className="role-card receive" onClick={handleStartReceive}>
                    <div className="role-icon">📥</div>
                    <h3>Receive Files</h3>
                    <p>On any device. Enter the sender's PIN and files appear instantly.</p>
                    <div className="role-arrow">→</div>
                </div>
            </div>
        </div>
    );

    const commonSidebar = () => {
        const activePeerCount = Object.keys(peers).filter(id => id !== deviceId).length;
        
        return (
            <div className="sidebar">
                <div className="sidebar-section">
                    <div className="sidebar-section-title">Session PIN</div>
                    <div className="pin-display">
                        <div className="pin-digits">{pin}</div>
                        <div className="pin-label">Share this with the receivers</div>
                    </div>
                </div>
                <div className="sidebar-section">
                    <div className="sidebar-section-title">Connected Devices ({activePeerCount})</div>
                    <div className="status-card">
                        <div className="status-icon">📡</div>
                        <div className="status-info">
                            <div className="status-title">{status}</div>
                            <div className="status-sub" style={{ fontSize: '11px', color: 'var(--muted)' }}>
                                {activePeerCount === 0 ? "Waiting for others..." : `${activePeerCount} other device(s) connected`}
                            </div>
                        </div>
                    </div>
                </div>
                {view === 'sender' && (
                    <div className="upload-zone" onClick={() => document.getElementById('file-input').click()}>
                        <div className="dz-icon">☁️</div>
                        <div className="dz-title">Click to upload</div>
                        <input type="file" id="file-input" style={{ display: 'none' }} onChange={onFileChange} />
                    </div>
                )}
                {uploadingFile && (
                    <div className="progress-wrap">
                        <div className="progress-info">
                            <span>{uploadingFile}</span>
                            <span>{uploadProgress}%</span>
                        </div>
                        <div className="progress-bar-track">
                            <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                    </div>
                )}
                <div className="sidebar-section">
                    <div className="sidebar-section-title">Files List</div>
                    <div className="files-list">
                        {files.map(f => (
                            <div key={f.file_id} className="file-item">
                                <div className="file-thumb">📄</div>
                                <div className="file-meta">
                                    <div className="file-name">{f.filename}</div>
                                    <div className="file-size">{f.size ? formatBytes(f.size) : 'Ready'}</div>
                                </div>
                                <div className="file-actions">
                                    <button className="file-btn view" title="View/Play" onClick={() => window.open(getViewUrl(f.file_id), '_blank')}>▶</button>
                                    <a href={getDownloadUrl(f.file_id)} download={f.filename} className="file-btn download" title="Download">⬇</a>
                                </div>
                            </div>
                        ))}
                        {files.length === 0 && <div style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'center', padding: '20px' }}>No files yet</div>}
                    </div>
                </div>
                <button className="exit-btn" onClick={() => window.location.reload()}>✕ Exit Session</button>
            </div>
        );
    };

    return (
        <div className="App">
            <div className="topbar">
                <div className="logo">Stream<span>Drop</span></div>
                <div className="badge">Radar Share</div>
            </div>
            {view === 'home' && renderHome()}
            {view !== 'home' && (
                <div className="page-body fade-up">
                    {pin ? commonSidebar() : (
                        <div className="sidebar">
                            <div className="sidebar-section">
                                <div className="sidebar-section-title">Join a Session</div>
                                <div className="connect-panel">
                                    <input 
                                        className="pin-input" 
                                        placeholder="000000" 
                                        value={pinInput}
                                        onChange={(e) => setPinInput(e.target.value)}
                                    />
                                    <button className="btn-primary" onClick={handleJoin}>Join</button>
                                </div>
                            </div>
                        </div>
                    )}
                    <Radar peers={peers} myId={deviceId} pin={pin} />
                </div>
            )}
        </div>
    );
}

export default App;
